import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';
import { useHistory } from 'react-router-dom';
import { usePhotoSeriesStore } from '../store/root.hook';
import { CameraMode } from '../store/root';
import { getDeviceOrientation, DeviceOrientationData } from 'utils';
import { useFullScreen } from 'hooks';

const useNativeCameraUpload = (captureViewUrl: string) => {
  const {
    dispatchHandleNativeCameraPhotoTaken,
    camera: { mode, fileInput }
  } = usePhotoSeriesStore();

  const history = useHistory();
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const { toggleFullScreen } = useFullScreen();

  const handleNativeCameraPhotoTaken = useCallback(async (fileChangeEvent: Event) => {
    const target = fileChangeEvent.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length < 1) return;

    const photo = files[0];
    let deviceOrientation: DeviceOrientationData | null = null;

    if (photoSeries.trackDeviceOrientationEnabled) {
      deviceOrientation = await getDeviceOrientation();
    }

    // Add gyroscope data to the photo
    dispatchHandleNativeCameraPhotoTaken(photo, deviceOrientation);

    // Reset event target value so we can reuse change event
    // https://stackoverflow.com/a/56258902
    target.value = '';

    // Return to full screen
    toggleFullScreen(false, true);

    history.push(captureViewUrl);
  }, [dispatchHandleNativeCameraPhotoTaken, history, captureViewUrl, photoSeries, toggleFullScreen]);

  useEffect(() => {
    if (mode !== CameraMode.Native || !fileInput) return;

    fileInput.addEventListener('change', handleNativeCameraPhotoTaken);
    return () => fileInput.removeEventListener('change', handleNativeCameraPhotoTaken);
  }, [mode, fileInput, handleNativeCameraPhotoTaken]);

  return {
    fileInput, mode
  };
};

export { useNativeCameraUpload };
